import { FunctionComponent } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { faTruck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { showErrorToast, showSuccessToast } from '@fff-web/fff-ui-shared';
import CopyButtonIcon from './CopyButtonIcon';
import * as styles from './TrackingNumber.module.scss';

export type TrackingDetailsType = {
  trackingNumber?: string;
  carrier?: string;
  carrierLogo?: string;
  carrierTrackingUrl?: string;
};

export type TrackingNumberProps = {
  trackingDetails: TrackingDetailsType;
};

export const TrackingNumber: FunctionComponent<TrackingNumberProps> = ({ trackingDetails }) => {
  const trackingUrl = trackingDetails?.carrierTrackingUrl;
  const handleCopyTrackingNumber = async () => {
    if (!trackingDetails.trackingNumber) return;

    try {
      await navigator.clipboard.writeText(trackingDetails.trackingNumber);
      showSuccessToast(
        'Success',
        `Tracking number ${trackingDetails.trackingNumber} copied to clipboard!`,
      );
    } catch (error) {
      showErrorToast(
        'Error',
        `Failed to copy text for tracking number ${trackingDetails.trackingNumber}`,
      );
      datadogLogs.logger.error(
        `Failed to copy text for tracking number ${trackingDetails.trackingNumber}`,
        {},
        error as Error,
      );
    }
  };

  return (
    <div className={styles.container}>
      <h1 className={styles.trackingNumberTitle}>Tracking Number</h1>
      <div className={styles.trackingNumberContainer}>
        <div className={styles.trackingCarrier}>
          {trackingDetails.carrierLogo && (
            <img
              src={trackingDetails.carrierLogo}
              alt={trackingDetails.carrier}
              data-testid="carrier-logo"
            />
          )}
          {!trackingDetails.carrierLogo && (
            <div className={styles.noCarrierImage} data-testid="carrier-no-logo">
              <FontAwesomeIcon icon={faTruck} />
            </div>
          )}
        </div>
        <div className={styles.trackingNumber}>{trackingDetails.trackingNumber}</div>
        <div className={styles.trackingCopyButton}>
          <button onClick={handleCopyTrackingNumber} data-testid="clipboard-copy-button">
            <CopyButtonIcon />
            <span>Copy</span>
          </button>
        </div>
      </div>
      <div className={styles.externalTrackingLinkContainer}>
        <a href={trackingUrl} target="_blank" rel="noreferrer">
          View tracking on carrier site
        </a>
      </div>
    </div>
  );
};
